import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, useReducedMotion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A logo is ideal, but a wordmark in our own type keeps the strip composed when one is missing
const Mark = ({ entry }) =>
  entry.logo_url ? (
    <img
      src={entry.logo_url}
      alt={entry.organization_name}
      loading="lazy"
      className="h-8 sm:h-9 md:h-11 w-auto max-w-[150px] sm:max-w-[190px] object-contain"
    />
  ) : (
    <span className="text-base md:text-lg font-heading font-semibold tracking-tight whitespace-nowrap">
      {entry.organization_name}
    </span>
  );

const RecognitionStrip = () => {
  const [entries, setEntries] = useState([]);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    axios
      .get(`${API}/recognition`)
      .then((res) => setEntries(Array.isArray(res.data) ? res.data : []))
      .catch(() => setEntries([]));
  }, []);

  if (!entries.length) return null;

  return (
    <section
      className="bg-white border-b border-black/[0.05] py-10 md:py-14"
      data-testid="recognition-strip"
      aria-label="Recognition and media coverage"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        <motion.p
          initial={reduceMotion ? false : { opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
          className="text-[11px] uppercase tracking-[0.22em] text-foreground-muted text-center"
        >
          Recognized &amp; Featured By
        </motion.p>

        <div className="relative mt-7 md:mt-9">
          {/* Edge fades only matter while the row scrolls on small screens */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-white to-transparent md:hidden z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent md:hidden z-10" />

          <div className="overflow-x-auto md:overflow-visible scrollbar-hide px-2 md:px-0">
            <div className="flex items-center gap-8 sm:gap-16 md:gap-24 w-max mx-auto">
            {entries.map((entry, i) => {
              const content = (
                <>
                  <Mark entry={entry} />
                  {entry.description && (
                    <span
                      role="tooltip"
                      className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-3 whitespace-nowrap rounded-full bg-[rgb(var(--foreground))] text-white text-[11px] px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 hidden md:block"
                    >
                      {entry.description}
                    </span>
                  )}
                </>
              );

              const shared =
                'group relative flex-shrink-0 flex items-center justify-center h-10 md:h-12 grayscale opacity-55 hover:grayscale-0 hover:opacity-100 transition-[filter,opacity] duration-500';

              return (
                <motion.div
                  key={entry.id}
                  initial={reduceMotion ? false : { opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{
                    duration: 0.6,
                    delay: reduceMotion ? 0 : 0.12 + i * 0.08,
                    ease: [0.22, 0.61, 0.36, 1],
                  }}
                  className="flex-shrink-0"
                >
                  {entry.reference_url ? (
                    <a
                      href={entry.reference_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={shared}
                      title={entry.description || entry.organization_name}
                      data-testid={`recognition-logo-${entry.id}`}
                    >
                      {content}
                    </a>
                  ) : (
                    <div className={shared} data-testid={`recognition-logo-${entry.id}`}>
                      {content}
                    </div>
                  )}
                </motion.div>
              );
            })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RecognitionStrip;
